import { t } from "@/i18n-js/instance";
import { useSupportWidgetContext } from "@/react/components/SupportWidget/SupportWidgetContext";
import { useUpgradePlanModal } from "@/react/components/shared/UpgradePlan";
import { planTiers } from "@/react/helpers/planHelpers";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import type { ResourceSection } from "../shared/types";
import helpSupportIcon from "./ask-for-help.svg";
import circleCommunityIcon from "./circle-community.svg";
import { createAcademyResource } from "./createAcademyResource";
import knowledgeBaseIcon from "./knowledge-base.svg";

const localeRoot = "community_onboarding.new_checklist.resources";

export type ResourcesVariant = "default" | "admin-dashboard";

interface UseResourceSectionsOptions {
  variant?: ResourcesVariant;
}

export const useResourceSections = ({
  variant = "default",
}: UseResourceSectionsOptions = {}): ResourceSection[] => {
  const { toggleWidget } = useSupportWidgetContext();
  const { isCommunityOnPaidPlan } = usePlanStatus();
  const upgradePlanModal = useUpgradePlanModal();

  const handleUpgrade = () => {
    void upgradePlanModal.show({
      planTier: planTiers.professional,
      showPlanDetails: true,
      source: "checklist_resources_section",
    });
  };

  const academyResource = createAcademyResource(
    isCommunityOnPaidPlan,
    handleUpgrade,
  );

  const knowledgeBaseResource: ResourceSection = {
    icon: knowledgeBaseIcon,
    title: t(`${localeRoot}.sections.knowledge_base.title`),
    description: t(`${localeRoot}.sections.knowledge_base.description`),
    iconAlt: t(`${localeRoot}.sections.knowledge_base.icon_alt`),
    link: "https://help.circle.so/",
  };

  const circleCommunityResource: ResourceSection = {
    icon: circleCommunityIcon,
    title: t(`${localeRoot}.sections.circle_community.title`),
    description: t(`${localeRoot}.sections.circle_community.description`),
    iconAlt: t(`${localeRoot}.sections.circle_community.icon_alt`),
    link: "https://community.circle.so/",
  };

  if (variant === "admin-dashboard") {
    return [knowledgeBaseResource, circleCommunityResource];
  }

  return [
    {
      icon: helpSupportIcon,
      title: t(`${localeRoot}.sections.help.title`),
      description: t(`${localeRoot}.sections.help.description`),
      iconAlt: t(`${localeRoot}.sections.help.icon_alt`),
      button: {
        label: t(`${localeRoot}.sections.help.button.label`),
        handleClick: () => {
          toggleWidget();
        },
      },
    },
    knowledgeBaseResource,
    circleCommunityResource,
    academyResource,
  ];
};
